import { gql } from '@apollo/client';
import {
  Box,
  Button,
  chakra,
  Flex,
  FormLabel,
  Heading,
  HStack,
  Radio,
  Stack,
  Text,
  useDisclosure,
  useRadioGroup,
  useToast,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ChangeDefaultContentSourceModal } from '~components/configurations/ConfigChangeDefaultContentSourceModal';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { ConfigDisplayDefaultContentSource_ConfigFragment } from '~components/configurations/ConfigurationDetail/playback/__generated__/ConfigDisplayDefaultContentSource.graphql';
import {
  useConfigContentSource,
  useContentSourceLabel,
} from '~components/configurations/useContentSource';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { isDefined } from '~utils/types';
import { checkStateToDisableTab } from '../ConfigDetailSettings';

interface Props {
  config: ConfigDisplayDefaultContentSource_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}
export function ConfigDisplayDefaultContentSource({ config, configStatus }: Props) {
  const changeDefaultContentSourceModal = useDisclosure();
  const analytics = useAnalyticsReporter();
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const { t } = useTranslation();
  const contentSource = config.contentSource?.default?.reported;

  const isInputContentSource = contentSource?.__typename === 'ConfigInputContentSource';

  const isInputContentSourceWithLastInputSource =
    isInputContentSource && contentSource?.source === 'LAST INPUT';

  const isInputContentSourceWithoutLastInputSource =
    isInputContentSource && contentSource?.source !== 'LAST INPUT';

  const { UpdateDefaultContentSource, isLoadingDefaultContentSource } = useConfigContentSource();

  const isLastInputSourceSupported = config.contentSource?.available?.some(
    (source) => source.__typename === 'ConfigInputContentSource' && source.source === 'LAST INPUT',
  );

  const handleChange = useCallback(
    async (value: string) => {
      try {
        if (value === 'LAST INPUT') {
          await UpdateDefaultContentSource(config, {
            source: value,
            __typename: 'ConfigInputContentSource',
          });
        } else if (value === 'OTHER') {
          const availableContentSource = config.contentSource?.available
            // Some apps cannot be set as a default content source. Filter them out.
            ?.filter((availableContentSource) => {
              if (
                availableContentSource.__typename === 'ConfigAppContentSource' &&
                isDefined(availableContentSource.isBootable)
              ) {
                return availableContentSource.isBootable;
              }

              return true;
            })
            .at(0);

          if (isNil(availableContentSource)) {
            throw new Error('Input source cannot be empty');
          }

          await UpdateDefaultContentSource(config, availableContentSource);
        }
        analytics.track('displaySettingUpdate', {
          group: 'playback',
          changeItem: 'defaultContentSource',
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('changeInputSourceErr'),
          description: fromError(err, 'UpdateDefaultContentSource'),
        });
      }
    },
    [analytics, UpdateDefaultContentSource, config, toast, t],
  );

  const { bookmarkLabel, mediaLabel, appLabel, headingLabel } = useContentSourceLabel(
    contentSource,
    config,
  );

  const defaultInputSources = [
    {
      value: 'LAST INPUT',
      label: t('lastInputSource'),
      isChecked: isInputContentSourceWithLastInputSource,
      isEnabled: true,
    },
    {
      value: 'OTHER',
      label: t('specificInputSource'),
      isChecked:
        contentSource?.__typename !== 'ConfigInputContentSource' ||
        isInputContentSourceWithoutLastInputSource,
      isEnabled: Boolean(
        config?.contentSource?.available && config?.contentSource?.available?.length > 0,
      ),
    },
  ];

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'defaultInputSource',
    defaultValue: defaultInputSources.find((s) => s.isChecked)?.value,
    onChange: handleChange,
  });

  const group = getRootProps();

  const hasConfigUpdatePermission = verifyUserPermissions([Permission.ConfigurationUpdate]);

  return (
    <>
      <Stack direction="row" spacing="8" alignItems="center">
        <Box flex="1" wordBreak="break-word">
          <FormLabel>
            {t('defaultInputSource')}
            <FormLabelPendingIndicator isPending={isLoadingDefaultContentSource} />
          </FormLabel>

          <Text color="gray.500" marginBottom="3">
            {t('defaultInputSourceText')}
          </Text>
          {isLastInputSourceSupported && (
            <Stack {...group}>
              {defaultInputSources.map(({ value, label, isChecked, isEnabled }) => {
                const radio = getRadioProps({ value });
                return (
                  <Radio
                    key={value}
                    {...radio}
                    isChecked={isChecked}
                    isDisabled={
                      checkStateToDisableTab(configStatus)
                        ? true
                        : !hasConfigUpdatePermission || !isEnabled
                    }
                  >
                    {label}
                  </Radio>
                );
              })}
            </Stack>
          )}
          {(isInputContentSourceWithoutLastInputSource ||
            contentSource?.__typename !== 'ConfigInputContentSource') && (
            <HStack spacing="5" alignItems="center" mt="2">
              <Flex
                direction="column"
                borderLeft="2px solid"
                borderColor="gray.100"
                pl="4"
                ml="8"
                minHeight="12"
                justifyContent="center"
                alignItems="left"
              >
                {isInputContentSource && !contentSource?.source ? (
                  t('notSet')
                ) : (
                  <>
                    <Heading size="sm" color="gray.900" mb="0" lineHeight="7">
                      {headingLabel}
                    </Heading>
                    {bookmarkLabel || mediaLabel || appLabel ? (
                      <chakra.span color="gray.500" fontWeight="normal">
                        {bookmarkLabel}
                        {mediaLabel}
                        {appLabel}
                      </chakra.span>
                    ) : null}
                  </>
                )}
              </Flex>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={changeDefaultContentSourceModal.onOpen}
                isDisabled={
                  checkStateToDisableTab(configStatus) ? true : !hasConfigUpdatePermission
                }
              >
                {t('change')}
              </Button>
            </HStack>
          )}
        </Box>
      </Stack>
      <ChangeDefaultContentSourceModal
        config={config}
        isOpen={changeDefaultContentSourceModal.isOpen}
        onCancel={changeDefaultContentSourceModal.onClose}
        onSuccess={changeDefaultContentSourceModal.onClose}
      />
    </>
  );
}

ConfigDisplayDefaultContentSource.graphql = {
  fragments: {
    ConfigDisplayDefaultContentSource_config: gql`
      fragment ConfigDisplayDefaultContentSource_config on ConfigurationModel {
        id
        ...ChangeConfigDefaultContentSourceModal_config
      }
    `,
  },
};
